import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Stack,
  TextField,
  MenuItem,
  Button,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import {
  useListAuditLogsQuery,
  useGetAuditStatisticsQuery,
} from '@store/api/auditLogApi';
import type { AuditLogFilters } from '../types/api';
import {
  useTable,
  SortingState,
} from '@tanstack/react-table';
import { features } from '../components/common/tableFeatures';
import ColumnSelector from '../components/common/ColumnSelector';
import {
  DataTable,
  DataTablePagination,
  DataTableToolbar,
} from '../components/common/DataTable';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { AUDIT_COLUMNS } from '../components/audit/auditColumns';
import {
  AUDIT_COLUMN_METADATA,
  AUDIT_DEFAULT_COLUMNS,
} from '../components/audit/auditColumnMetadata';

const AuditLogPage: React.FC = () => {
  const [filters, setFilters] = useState<AuditLogFilters>({
    limit: 50,
    offset: 0,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data: auditData, isLoading, error } = useListAuditLogsQuery(filters);
  const { data: statistics } = useGetAuditStatisticsQuery();

  const handleFilterChange = (field: keyof AuditLogFilters, value: any) => {
    setFilters({ ...filters, [field]: value, offset: 0 });
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilters({ ...filters, offset: newPage * rowsPerPage });
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setFilters({ ...filters, limit: newRowsPerPage, offset: 0 });
  };

  const {
    visibleColumns,
    toggleColumn,
    resetToDefault,
    applyPreset,
    columnVisibility,
  } = useColumnVisibility({
    columns: AUDIT_COLUMN_METADATA,
    storageKey: 'auditLog.columnVisibility.v1',
    defaultColumns: AUDIT_DEFAULT_COLUMNS,
  });

  const table = useTable({
    features,
    data: auditData?.logs ?? [],
    columns: AUDIT_COLUMNS,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    getRowId: (row) => row.id,
    manualPagination: true,
  });

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Audit Log
      </Typography>

      {/* Statistics Cards */}
      {statistics && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Card sx={{ flex: '1 1 300px' }}>
            <CardContent>
              <Typography variant="h6" sx={{
                color: 'text.secondary'
              }}>
                Total Events
              </Typography>
              <Typography variant="h3">{statistics.total_events}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 300px' }}>
            <CardContent>
              <Typography variant="h6" sx={{
                color: 'text.secondary'
              }}>
                Event Types
              </Typography>
              <Typography variant="h3">
                {statistics.events_by_action ? Object.keys(statistics.events_by_action).length : 0}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 300px' }}>
            <CardContent>
              <Typography variant="h6" sx={{
                color: 'text.secondary'
              }}>
                Active Users
              </Typography>
              <Typography variant="h3">{statistics.top_users?.length || 0}</Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Filters */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} sx={{
            alignItems: 'center'
          }}>
            <FilterIcon />
            <Typography>Filters</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="User ID"
                sx={{ flex: '1 1 200px' }}
                value={filters.user_id || ''}
                onChange={(e) => handleFilterChange('user_id', e.target.value || undefined)}
                size="small"
              />
              <TextField
                label="Action"
                sx={{ flex: '1 1 200px' }}
                select
                value={filters.action || ''}
                onChange={(e) => handleFilterChange('action', e.target.value || undefined)}
                size="small"
              >
                <MenuItem value="">All Actions</MenuItem>
                <MenuItem value="device_verification_started">Device Verification Started</MenuItem>
                <MenuItem value="device_verified">Device Verified</MenuItem>
                <MenuItem value="device_rejected">Device Rejected</MenuItem>
                <MenuItem value="command_sent">Command Sent</MenuItem>
                <MenuItem value="user_created">User Created</MenuItem>
                <MenuItem value="user_updated">User Updated</MenuItem>
                <MenuItem value="user_deactivated">User Deactivated</MenuItem>
              </TextField>
              <TextField
                label="Resource Type"
                sx={{ flex: '1 1 200px' }}
                select
                value={filters.resource_type || ''}
                onChange={(e) => handleFilterChange('resource_type', e.target.value || undefined)}
                size="small"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="device">Device</MenuItem>
                <MenuItem value="command">Command</MenuItem>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="enrollment_token">Enrollment Token</MenuItem>
              </TextField>
              <Button
                variant="outlined"
                sx={{ flex: '0 1 150px' }}
                onClick={() => {
                  setFilters({ limit: 50, offset: 0 });
                  setPage(0);
                }}
              >
                Clear Filters
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Start Date"
                type="datetime-local"
                sx={{ flex: '1 1 300px' }}
                value={filters.start_date || ''}
                onChange={(e) => handleFilterChange('start_date', e.target.value || undefined)}
                size="small"
                slotProps={{
                  inputLabel: { shrink: true }
                }}
              />
              <TextField
                label="End Date"
                type="datetime-local"
                sx={{ flex: '1 1 300px' }}
                value={filters.end_date || ''}
                onChange={(e) => handleFilterChange('end_date', e.target.value || undefined)}
                size="small"
                slotProps={{
                  inputLabel: { shrink: true }
                }}
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load audit logs
        </Alert>
      )}

      {/* Audit Log Table */}
      <Paper>
        <DataTableToolbar
          actions={
            <ColumnSelector
              columns={AUDIT_COLUMN_METADATA}
              visibleColumns={visibleColumns}
              onColumnToggle={toggleColumn}
              onReset={resetToDefault}
              onPresetSelect={applyPreset}
            />
          }
        />

        <DataTable
          table={table}
          ariaLabel="Audit log"
          isLoading={isLoading}
          emptyMessage="No audit logs found"
        />

        {auditData && (
          <DataTablePagination
            count={auditData.total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[25, 50, 100]}
          />
        )}
      </Paper>
    </Box>
  );
};

export default AuditLogPage;
